import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const BRAND = {
  name: "Go!Foton",
  website: "gofoton.com",
  logoUrl:
    "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FGoFoton%20Logo-k%20(R).png?alt=media&token=6fa4c257-8569-4f86-af93-320f2b64a527",
};

const C = {
  ink: "111827",
  text: "374151",
  muted: "6B7280",
  faint: "9CA3AF",
  line: "E5E7EB",
  soft: "F8FAFC",
  orange: "D94829",
  plum: "85246D",
  green: "8CC540",
  red: "B91C1C",
  white: "FFFFFF",
};

const PDF_MARGIN = 42;
const PDF_BOTTOM = 742;

let logoPromise = null;

const num = (value = 0) => Number(value || 0).toLocaleString();

const pct = (value = 0, digits = 1) => `${Number(value || 0).toFixed(digits)}%`;

const duration = (value = 0) => {
  const total = Math.max(0, Math.round(Number(value || 0)));

  const minutes = Math.floor(total / 60);
  const seconds = total % 60;

  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const safeName = (value = "analytics") =>
  String(value || "analytics")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "analytics";

const clip = (value = "", max = 42) => {
  const text = String(value || "");

  return text.length > max ? `${text.slice(0, max - 1).trim()}…` : text;
};

const cleanRows = (rows, max = 10) => (Array.isArray(rows) ? rows.filter(Boolean).slice(0, max) : []);

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const generatedDate = (value) =>
  new Date(value || Date.now()).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const reportRange = (data) => {
  const start = formatDate(data?.range?.start);

  const end = formatDate(data?.range?.end);

  if (!start || !end) {
    return data?.range?.label || "Selected period";
  }

  return start === end ? start : `${start} – ${end}`;
};

const change = (current = 0, previous = 0) => {
  const now = Number(current || 0);

  const before = Number(previous || 0);

  if (!before && !now) return 0;
  if (!before) return null;

  return ((now - before) / before) * 100;
};

const changeLabel = (current = 0, previous = 0) => {
  const value = change(current, previous);

  if (value === null) {
    return "New vs. prior period";
  }

  const arrow = value >= 0 ? "▲" : "▼";

  return `${arrow} ${Math.abs(value).toFixed(1)}% vs. prior period`;
};

const changeHex = (current = 0, previous = 0) => {
  const value = change(current, previous);

  if (value === null || value === 0) {
    return C.muted;
  }

  return value > 0 ? "15803D" : C.red;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);

    reader.onerror = () => reject(reader.error || new Error("Could not read the report logo."));

    reader.readAsDataURL(blob);
  });

const loadLogo = async (url) => {
  const source = url || BRAND.logoUrl;

  if (!source) return null;

  if (!logoPromise) {
    logoPromise = fetch(source, {
      mode: "cors",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Logo request failed.");
        }

        return response.blob();
      })
      .then(blobToDataUrl)
      .catch((error) => {
        console.warn("Analytics report logo could not be loaded:", error);

        return null;
      });
  }

  return logoPromise;
};

const rgb = (hex) => {
  const clean = String(hex).replace("#", "");

  return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
};

const pdfText = (pdf, hex) => pdf.setTextColor(...rgb(hex));

const pdfFill = (pdf, hex) => pdf.setFillColor(...rgb(hex));

const pdfLine = (pdf, hex) => pdf.setDrawColor(...rgb(hex));

const addPdfLogo = (pdf, logo, x = PDF_MARGIN, y = 27, width = 108) => {
  if (logo) {
    try {
      pdf.addImage(logo, "PNG", x, y, width, 31, undefined, "FAST");

      return;
    } catch (error) {
      console.warn("PDF logo could not be drawn:", error);
    }
  }

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(17);

  pdfText(pdf, C.ink);

  pdf.text(BRAND.name, x, y + 21);
};

const pdfHeader = (pdf, title, data, logo) => {
  addPdfLogo(pdf, logo);

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(15);

  pdfText(pdf, C.ink);

  pdf.text(title, PDF_MARGIN, 67);

  pdf.setFont("helvetica", "normal");

  pdf.setFontSize(8.5);

  pdfText(pdf, C.muted);

  pdf.text(reportRange(data), 570, 67, {
    align: "right",
  });

  pdfLine(pdf, C.line);

  pdf.setLineWidth(0.6);

  pdf.line(PDF_MARGIN, 80, 570, 80);
};

const pdfPage = (pdf, title, data, logo) => {
  pdf.addPage("letter", "portrait");

  pdfHeader(pdf, title, data, logo);

  return 108;
};

const pdfSection = (pdf, title, subtitle, y) => {
  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(17);

  pdfText(pdf, C.ink);

  pdf.text(title, PDF_MARGIN, y);

  if (!subtitle) {
    return y + 21;
  }

  pdf.setFont("helvetica", "normal");

  pdf.setFontSize(9);

  pdfText(pdf, C.muted);

  const lines = pdf.splitTextToSize(subtitle, 520);

  pdf.text(lines, PDF_MARGIN, y + 17);

  return y + 18 + lines.length * 10;
};

const pdfKpis = (pdf, items, y, columns = 3) => {
  const width = 528 / columns;

  const rowHeight = 68;

  items.forEach((item, index) => {
    const col = index % columns;

    const row = Math.floor(index / columns);

    const x = PDF_MARGIN + col * width;

    const top = y + row * rowHeight;

    pdf.setFont("helvetica", "bold");

    pdf.setFontSize(7.2);

    pdfText(pdf, C.muted);

    pdf.text(String(item.label || "").toUpperCase(), x, top);

    pdf.setFontSize(20);

    pdfText(pdf, C.ink);

    pdf.text(String(item.value ?? "—"), x, top + 24);

    if (item.previous !== undefined) {
      pdf.setFont("helvetica", "normal");

      pdf.setFontSize(7.2);

      pdfText(pdf, changeHex(item.current, item.previous));

      pdf.text(changeLabel(item.current, item.previous), x, top + 40);
    }

    pdfLine(pdf, C.line);

    pdf.setLineWidth(0.35);

    pdf.line(x, top + 51, x + width - 15, top + 51);
  });

  return y + Math.ceil(items.length / columns) * rowHeight;
};

const pdfBullets = (pdf, items, y, width = 520) => {
  let cursor = y;

  items.forEach((item) => {
    pdfFill(pdf, C.orange);

    pdf.circle(PDF_MARGIN + 3, cursor - 2, 2.2, "F");

    pdf.setFont("helvetica", "normal");

    pdf.setFontSize(9.3);

    pdfText(pdf, C.text);

    const lines = pdf.splitTextToSize(String(item), width - 20);

    pdf.text(lines, PDF_MARGIN + 15, cursor);

    cursor += lines.length * 12 + 9;
  });

  return cursor;
};

const pdfBars = (pdf, title, rows, x, y, width, maxRows = 7) => {
  const values = cleanRows(rows, maxRows);

  const maximum = Math.max(1, ...values.map((row) => Number(row.value ?? row.sessions ?? 0)));

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(10.5);

  pdfText(pdf, C.ink);

  pdf.text(title, x, y);

  let cursor = y + 18;

  values.forEach((row, index) => {
    const value = Number(row.value ?? row.sessions ?? 0);

    pdf.setFont("helvetica", "normal");

    pdf.setFontSize(7.6);

    pdfText(pdf, C.text);

    pdf.text(clip(row.label || row.name || "Unknown", 28), x, cursor + 7);

    pdfFill(pdf, C.soft);

    pdf.roundedRect(x, cursor + 12, width - 54, 6, 3, 3, "F");

    pdfFill(pdf, index % 2 ? C.orange : C.plum);

    pdf.roundedRect(x, cursor + 12, Math.max(2, ((width - 54) * value) / maximum), 6, 3, 3, "F");

    pdf.setFont("helvetica", "bold");

    pdfText(pdf, C.ink);

    pdf.text(num(value), x + width, cursor + 17, {
      align: "right",
    });

    cursor += 30;
  });

  if (!values.length) {
    pdf.setFont("helvetica", "normal");

    pdf.setFontSize(8.5);

    pdfText(pdf, C.faint);

    pdf.text("No data available for this period.", x, cursor + 7);
  }

  return cursor;
};

const pdfTrafficChart = (pdf, rows, x, y, width, height) => {
  const values = cleanRows(rows, 90);

  const maximum = Math.max(1, ...values.flatMap((row) => [Number(row.sessions || 0), Number(row.pageViews || 0)]));

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(10.5);

  pdfText(pdf, C.ink);

  pdf.text("Traffic trend", x, y);

  const top = y + 18;

  const bottom = top + height;

  pdfLine(pdf, C.line);

  pdf.setLineWidth(0.5);

  pdf.line(x, bottom, x + width, bottom);

  if (values.length > 1) {
    const drawSeries = (key, color) => {
      pdfLine(pdf, color);

      pdf.setLineWidth(1.5);

      for (let index = 1; index < values.length; index += 1) {
        const previous = values[index - 1];

        const current = values[index];

        const x1 = x + ((index - 1) / (values.length - 1)) * width;

        const x2 = x + (index / (values.length - 1)) * width;

        const y1 = bottom - (Number(previous[key] || 0) / maximum) * height;

        const y2 = bottom - (Number(current[key] || 0) / maximum) * height;

        pdf.line(x1, y1, x2, y2);
      }
    };

    drawSeries("sessions", C.orange);

    drawSeries("pageViews", C.plum);
  }

  pdf.setFont("helvetica", "normal");

  pdf.setFontSize(7);

  pdfText(pdf, C.muted);

  if (values.length) {
    pdf.text(String(values[0].date || "").slice(5), x, bottom + 12);

    pdf.text(String(values[values.length - 1].date || "").slice(5), x + width, bottom + 12, {
      align: "right",
    });
  }

  pdfFill(pdf, C.orange);

  pdf.circle(x + width - 118, y - 3, 3, "F");

  pdfText(pdf, C.muted);

  pdf.text("Sessions", x + width - 109, y);

  pdfFill(pdf, C.plum);

  pdf.circle(x + width - 55, y - 3, 3, "F");

  pdf.text("Page views", x + width - 46, y);
};

const pdfTable = (pdf, startY, headers, body, widths = {}, fontSize = 7.4) => {
  autoTable(pdf, {
    startY,

    margin: {
      left: PDF_MARGIN,
      right: PDF_MARGIN,
      bottom: 54,
    },

    head: [headers],

    body,

    theme: "plain",

    styles: {
      font: "helvetica",
      fontSize,
      textColor: rgb(C.text),
      cellPadding: 5,
      lineColor: rgb(C.line),
      lineWidth: {
        bottom: 0.35,
      },
      overflow: "linebreak",
      valign: "middle",
    },

    headStyles: {
      fillColor: rgb(C.soft),
      textColor: rgb(C.ink),
      fontStyle: "bold",
      fontSize: fontSize - 0.2,
      lineWidth: {
        bottom: 0.7,
      },
    },

    alternateRowStyles: {
      fillColor: [252, 252, 252],
    },

    columnStyles: widths,
  });

  return pdf.lastAutoTable?.finalY || startY;
};

const executiveInsights = (data) => {
  const output = [];

  const visitors = data.overview?.visitors || {};

  const visitorChange = change(visitors.current, visitors.previous);

  if (visitorChange !== null) {
    output.push(
      `Unique visitors ${visitorChange >= 0 ? "increased" : "decreased"} ${Math.abs(visitorChange).toFixed(
        1
      )}% compared with the prior period.`
    );
  }

  const source = data.acquisition?.sources?.[0];

  if (source) {
    output.push(`${source.label} was the leading acquisition source with ${num(source.value)} sessions.`);
  }

  const page = data.pages?.[0];

  if (page) {
    output.push(`${page.page} was the most-viewed page with ${num(page.views)} views.`);
  }

  const product = data.products?.[0];

  if (product) {
    output.push(`${product.name} led product interest with ${num(product.views)} views.`);
  }

  if (Number(data.search?.searches || 0)) {
    output.push(
      `Site search produced ${num(data.search.searches)} searches with a ${pct(
        data.search.ctr
      )} result click-through rate.`
    );
  }

  return output.slice(0, 5);
};

const watchItems = (data) => {
  const output = [];

  const searches = Number(data.search?.searches || 0);

  const zeroResults = Number(data.search?.zeroResults || 0);

  const zeroRate = searches ? (zeroResults / searches) * 100 : 0;

  if (zeroRate >= 10) {
    output.push(`${pct(zeroRate)} of site searches returned no results.`);
  }

  if (Number(data.tools?.invalid || 0)) {
    output.push(`${num(data.tools.invalid)} complete Part Number Lookup attempts were invalid.`);
  }

  if (Number(data.exits?.eligible || 0)) {
    const passiveRate = (Number(data.exits.passive || 0) / Number(data.exits.eligible || 1)) * 100;

    if (passiveRate >= 50) {
      output.push(`${pct(passiveRate)} of eligible exits ended without a tracked final action.`);
    }
  }

  if (Number(data.search?.plumaFeedbackTotal || 0) >= 5 && Number(data.search?.plumaSatisfaction || 0) < 70) {
    output.push(`Pluma's helpful-response rate was ${pct(data.search.plumaSatisfaction)}.`);
  }

  return output.length ? output.slice(0, 4) : ["No major reporting alerts were triggered for this period."];
};

const renderPdfCover = (pdf, data, logo) => {
  pdfFill(pdf, C.soft);

  pdf.rect(0, 0, 612, 792, "F");

  pdfFill(pdf, C.orange);

  pdf.rect(0, 0, 612, 9, "F");

  addPdfLogo(pdf, logo, 56, 64, 150);

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(31);

  pdfText(pdf, C.ink);

  pdf.text("Website Analytics", 56, 198);

  pdf.text("Performance Report", 56, 237);

  pdf.setFont("helvetica", "normal");

  pdf.setFontSize(14);

  pdfText(pdf, C.muted);

  pdf.text(reportRange(data), 56, 281);

  pdfLine(pdf, C.line);

  pdf.setLineWidth(1);

  pdf.line(56, 311, 556, 311);

  pdf.setFontSize(10);

  pdfText(pdf, C.text);

  pdf.text("Prepared for Sales, Marketing, Executive, and Web teams", 56, 342);

  pdf.text(`Generated ${generatedDate(data.generatedAt)}`, 56, 365);

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(10);

  pdfText(pdf, C.plum);

  pdf.text("CONFIDENTIAL · INTERNAL USE", 56, 696);

  pdf.setFont("helvetica", "normal");

  pdf.setFontSize(9);

  pdfText(pdf, C.muted);

  pdf.text(BRAND.website, 56, 720);
};

const renderPdfExecutive = (pdf, data, logo) => {
  let y = pdfPage(pdf, "Executive Summary", data, logo);

  y = pdfSection(pdf, "Performance at a glance", "Current period compared with the immediately preceding period.", y);

  const o = data.overview || {};

  y = pdfKpis(
    pdf,
    [
      {
        label: "Visitors",
        value: num(o.visitors?.current),
        current: o.visitors?.current,
        previous: o.visitors?.previous,
      },
      {
        label: "Sessions",
        value: num(o.sessions?.current),
        current: o.sessions?.current,
        previous: o.sessions?.previous,
      },
      {
        label: "Page views",
        value: num(o.pageViews?.current),
        current: o.pageViews?.current,
        previous: o.pageViews?.previous,
      },
      {
        label: "Product views",
        value: num(o.productViews?.current),
        current: o.productViews?.current,
        previous: o.productViews?.previous,
      },
      {
        label: "Downloads",
        value: num(o.downloads?.current),
        current: o.downloads?.current,
        previous: o.downloads?.previous,
      },
      {
        label: "Form submissions",
        value: num(o.formSubmissions?.current),
        current: o.formSubmissions?.current,
        previous: o.formSubmissions?.previous,
      },
    ],
    y + 16,
    3
  );

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(11);

  pdfText(pdf, C.ink);

  pdf.text("Key findings", PDF_MARGIN, y + 5);

  y = pdfBullets(pdf, executiveInsights(data), y + 25);

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(11);

  pdfText(pdf, C.ink);

  pdf.text("Items to monitor", PDF_MARGIN, y + 8);

  pdfBullets(pdf, watchItems(data), y + 28);
};

const renderPdfAcquisition = (pdf, data, logo) => {
  let y = pdfPage(pdf, "Audience & Acquisition", data, logo);

  const a = data.acquisition || {};

  const o = data.overview || {};

  y = pdfKpis(
    pdf,
    [
      {
        label: "Visitors",
        value: num(o.visitors?.current),
        current: o.visitors?.current,
        previous: o.visitors?.previous,
      },
      {
        label: "New visitors",
        value: num(o.newVisitors?.current),
        current: o.newVisitors?.current,
        previous: o.newVisitors?.previous,
      },
      {
        label: "Returning visitors",
        value: num(o.returningVisitors?.current),
        current: o.returningVisitors?.current,
        previous: o.returningVisitors?.previous,
      },
      {
        label: "Organic",
        value: num(a.sourceTotals?.organic),
      },
      {
        label: "Direct",
        value: num(a.sourceTotals?.direct),
      },
      {
        label: "Referral",
        value: num(a.sourceTotals?.referral),
      },
    ],
    y,
    3
  );

  pdfTrafficChart(pdf, data.trends?.traffic || [], PDF_MARGIN, y + 2, 528, 105);

  pdfBars(pdf, "Top acquisition sources", a.sources || [], PDF_MARGIN, y + 156, 245, 6);

  pdfBars(pdf, "Top countries", a.countries || [], 323, y + 156, 245, 6);

  pdfTable(
    pdf,
    y + 352,
    ["Landing page", "Sessions", "Visitors", "Top source"],
    cleanRows(a.landingPages, 6).map((row) => [
      clip(row.page, 46),
      num(row.sessions),
      num(row.uniqueVisitors),
      clip(row.topSource, 24),
    ]),
    {
      0: {
        cellWidth: 260,
      },
      1: {
        halign: "right",
      },
      2: {
        halign: "right",
      },
    }
  );
};

const renderPdfContent = (pdf, data, logo) => {
  let y = pdfPage(pdf, "Content & Product Performance", data, logo);

  y = pdfSection(pdf, "Top website content", "Highest-performing pages and products in the selected period.", y);

  y = pdfTable(
    pdf,
    y + 5,
    ["Page", "Visitors", "Views", "Avg. active", "Avg. scroll"],
    cleanRows(data.pages, 10).map((row) => [
      clip(row.page, 48),
      num(row.uniqueVisitors),
      num(row.views),
      duration(row.avgActiveSeconds),
      pct(row.avgScroll, 0),
    ]),
    {
      0: {
        cellWidth: 260,
      },
      1: {
        halign: "right",
      },
      2: {
        halign: "right",
      },
      3: {
        halign: "right",
      },
      4: {
        halign: "right",
      },
    }
  );

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(12);

  pdfText(pdf, C.ink);

  pdf.text("Top products", PDF_MARGIN, y + 30);

  pdfTable(
    pdf,
    y + 42,
    ["Product", "Family", "Visitors", "Views", "Downloads", "Avg. time"],
    cleanRows(data.products, 10).map((row) => [
      clip(row.name, 34),
      clip(row.family, 24),
      num(row.visitors),
      num(row.views),
      num(row.downloads),
      duration(row.avgTimeSeconds),
    ]),
    {
      0: {
        cellWidth: 170,
      },
      1: {
        cellWidth: 125,
      },
      2: {
        halign: "right",
      },
      3: {
        halign: "right",
      },
      4: {
        halign: "right",
      },
      5: {
        halign: "right",
      },
    }
  );
};

const renderPdfEngagement = (pdf, data, logo) => {
  let y = pdfPage(pdf, "Engagement & Conversion", data, logo);

  const e = data.engagement || {};

  const o = data.overview || {};

  y = pdfKpis(
    pdf,
    [
      {
        label: "Avg. active time",
        value: duration(o.avgActiveSeconds?.current),
        current: o.avgActiveSeconds?.current,
        previous: o.avgActiveSeconds?.previous,
      },
      {
        label: "Pages / session",
        value: Number(o.pagesPerSession?.current || 0).toFixed(1),
        current: o.pagesPerSession?.current,
        previous: o.pagesPerSession?.previous,
      },
      {
        label: "Downloads",
        value: num(e.documentDownloads),
      },
      {
        label: "Button clicks",
        value: num(e.buttonClicks),
      },
      {
        label: "Email clicks",
        value: num(e.emailClicks),
      },
      {
        label: "Phone clicks",
        value: num(e.phoneClicks),
      },
    ],
    y,
    3
  );

  pdfBars(pdf, "Engagement by weekday", e.weekday || [], PDF_MARGIN, y + 8, 245, 7);

  pdfBars(pdf, "Top downloads", e.topDownloads || [], 323, y + 8, 245, 7);

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(12);

  pdfText(pdf, C.ink);

  pdf.text("Website conversion funnel", PDF_MARGIN, y + 246);

  pdfTable(
    pdf,
    y + 258,
    ["Stage", "Sessions", "From prior stage", "Overall"],
    cleanRows(data.funnel, 8).map((row) => [
      row.label,
      num(row.current),
      pct(row.conversion),
      pct(row.overallConversion),
    ]),
    {
      0: {
        cellWidth: 255,
      },
      1: {
        halign: "right",
      },
      2: {
        halign: "right",
      },
      3: {
        halign: "right",
      },
    }
  );
};

const renderPdfJourneys = (pdf, data, logo) => {
  let y = pdfPage(pdf, "Customer Journeys", data, logo);

  const j = data.journeys || {};

  y = pdfKpis(
    pdf,
    [
      {
        label: "Visitors",
        value: num(j.totalVisitors),
      },
      {
        label: "Returning",
        value: num(j.returningVisitors),
      },
      {
        label: "Return rate",
        value: pct(j.returningRate),
      },
      {
        label: "Avg. sessions",
        value: Number(j.avgSessions || 0).toFixed(1),
      },
      {
        label: "Avg. pages",
        value: Number(j.avgPages || 0).toFixed(1),
      },
      {
        label: "Avg. active time",
        value: duration(j.avgActiveSeconds),
      },
    ],
    y,
    3
  );

  pdfBars(pdf, "Journey classifications", j.categories || [], PDF_MARGIN, y + 8, 528, 7);

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(12);

  pdfText(pdf, C.ink);

  pdf.text("Three longest active journeys", PDF_MARGIN, y + 260);

  pdfTable(
    pdf,
    y + 273,
    ["Visitor", "Type", "Sessions", "Pages", "Active", "Source", "Journey path"],
    cleanRows(j.longest, 3).map((row) => [
      row.visitor,
      row.category,
      num(row.sessions),
      num(row.pages),
      duration(row.activeSeconds),
      clip(row.source, 20),
      clip((row.path || []).join(" → "), 70),
    ]),
    {
      0: {
        cellWidth: 62,
      },
      1: {
        cellWidth: 65,
      },
      2: {
        halign: "right",
      },
      3: {
        halign: "right",
      },
      4: {
        cellWidth: 48,
      },
      5: {
        cellWidth: 65,
      },
      6: {
        cellWidth: 190,
      },
    },
    6.7
  );
};

const renderPdfSearchTools = (pdf, data, logo) => {
  let y = pdfPage(pdf, "Search, Pluma & Website Tools", data, logo);

  const s = data.search || {};

  const t = data.tools || {};

  y = pdfKpis(
    pdf,
    [
      {
        label: "Searches",
        value: num(s.searches),
      },
      {
        label: "Search CTR",
        value: pct(s.ctr),
      },
      {
        label: "Zero results",
        value: num(s.zeroResults),
      },
      {
        label: "Pluma messages",
        value: num(s.plumaMessages),
      },
      {
        label: "Pluma helpful",
        value: s.plumaFeedbackTotal ? pct(s.plumaSatisfaction) : "—",
      },
      {
        label: "Tool completion",
        value: pct(t.completionRate),
      },
    ],
    y,
    3
  );

  pdfBars(pdf, "Top search terms", s.topTerms || [], PDF_MARGIN, y + 8, 245, 7);

  pdfBars(pdf, "Most-clicked search results", s.topResults || [], 323, y + 8, 245, 7);

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(12);

  pdfText(pdf, C.ink);

  pdf.text("Part Number Lookup", PDF_MARGIN, y + 246);

  pdfTable(
    pdf,
    y + 259,
    ["Metric", "Value"],
    [
      ["Unique tool users", num(t.users)],
      ["Lookups started", num(t.starts)],
      ["Valid completions", num(t.completions)],
      ["Completion rate", pct(t.completionRate)],
      ["Valid attempt rate", pct(t.successRate)],
      ["Invalid full attempts", num(t.invalid)],
      ["Average time to valid", duration(t.avgSeconds)],
      ["Part numbers copied", num(t.copies)],
    ],
    {
      0: {
        cellWidth: 380,
      },
      1: {
        halign: "right",
      },
    }
  );
};

const renderPdfExits = (pdf, data, logo) => {
  let y = pdfPage(pdf, "Exit Behavior & Actions", data, logo);

  const e = data.exits || {};

  y = pdfKpis(
    pdf,
    [
      {
        label: "Eligible exits",
        value: num(e.eligible),
      },
      {
        label: "Tracked actions",
        value: num(e.tracked),
      },
      {
        label: "Contact exits",
        value: num(e.contact),
      },
      {
        label: "Passive exits",
        value: num(e.passive),
      },
      {
        label: "Avg. pages",
        value: Number(e.avgPages || 0).toFixed(1),
      },
      {
        label: "Avg. active",
        value: duration(e.avgActiveSeconds),
      },
    ],
    y,
    3
  );

  pdfBars(pdf, "Why sessions ended", e.reasons || [], PDF_MARGIN, y + 8, 245, 7);

  pdfBars(pdf, "Top exit pages", e.topPages || [], 323, y + 8, 245, 7);

  pdf.setFont("helvetica", "bold");

  pdf.setFontSize(12);

  pdfText(pdf, C.ink);

  pdf.text("Recommended follow-up", PDF_MARGIN, y + 264);

  pdfBullets(pdf, watchItems(data), y + 285);
};

const stampPdfFooters = (pdf, data) => {
  const pages = pdf.getNumberOfPages();

  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page);

    pdfLine(pdf, C.line);

    pdf.setLineWidth(0.45);

    pdf.line(PDF_MARGIN, PDF_BOTTOM - 12, 570, PDF_BOTTOM - 12);

    pdf.setFont("helvetica", "normal");

    pdf.setFontSize(7.4);

    pdfText(pdf, C.muted);

    pdf.text(`${BRAND.name} Website Analytics · Internal`, PDF_MARGIN, PDF_BOTTOM);

    pdf.text(`Generated ${generatedDate(data.generatedAt)}`, 306, PDF_BOTTOM, {
      align: "center",
    });

    pdf.text(`Page ${page} of ${pages}`, 570, PDF_BOTTOM, {
      align: "right",
    });
  }
};

export async function exportAnalyticsPdf({ data }) {
  if (!data) {
    throw new Error("Analytics report data is not available yet.");
  }

  const logo = await loadLogo(data.brand?.logoUrl);

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
    compress: true,
    putOnlyUsedFonts: true,
  });

  renderPdfCover(pdf, data, logo);

  renderPdfExecutive(pdf, data, logo);

  renderPdfAcquisition(pdf, data, logo);

  renderPdfContent(pdf, data, logo);

  renderPdfEngagement(pdf, data, logo);

  renderPdfJourneys(pdf, data, logo);

  renderPdfSearchTools(pdf, data, logo);

  renderPdfExits(pdf, data, logo);

  stampPdfFooters(pdf, data);

  pdf.setProperties({
    title: `${BRAND.name} Website Analytics - ${data.range?.label || "Selected Period"}`,
    subject: "Website analytics performance report",
    author: BRAND.name,
    creator: BRAND.name,
  });

  pdf.save(`gofoton-analytics-${safeName(data.range?.label || "report")}.pdf`);
}
/* ==========================================================================
   16:9 SLIDE IMAGE EXPORT
   ========================================================================== */

const SLIDE_WIDTH = 1920;
const SLIDE_HEIGHT = 1080;

const SLIDE_TITLES = {
  overview: "Analytics Overview",
  pages: "Page Performance",
  products: "Product Performance",
  acquisition: "Audience & Acquisition",
  engagement: "Engagement",
  exits: "Exit Behavior",
  funnel: "Conversion Funnel",
  journeys: "Customer Journeys",
  tools: "Website Tools",
  search: "Search & Pluma",
};

const canvasToBlob = (canvas, type = "image/jpeg", quality = 0.94) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("The analytics slide image could not be generated."));
        }
      },
      type,
      quality
    );
  });

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;

  document.body.appendChild(anchor);

  anchor.click();
  anchor.remove();

  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1500);
};

const dataUrlToImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);

    image.onerror = () => reject(new Error("The Go!Foton logo could not be loaded."));

    image.src = dataUrl;
  });

const drawRoundedRect = (ctx, x, y, width, height, radius = 12) => {
  ctx.beginPath();

  ctx.roundRect(x, y, width, height, radius);

  ctx.fill();
};

const drawSlideText = (ctx, text, x, y, { fontSize = 24, weight = 400, color = "#374151", align = "left" } = {}) => {
  ctx.font = `${weight} ${fontSize}px Arial, Helvetica, sans-serif`;

  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = "top";

  ctx.fillText(String(text ?? ""), x, y);
};

const wrapCanvasText = (ctx, text, x, y, maxWidth, lineHeight, maxLines = 2) => {
  const words = String(text || "").split(/\s+/);

  let line = "";
  let lineNumber = 0;

  for (let index = 0; index < words.length; index += 1) {
    const test = line ? `${line} ${words[index]}` : words[index];

    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y + lineNumber * lineHeight);

      line = words[index];

      lineNumber += 1;

      if (lineNumber >= maxLines - 1) {
        break;
      }
    } else {
      line = test;
    }
  }

  if (line && lineNumber < maxLines) {
    ctx.fillText(line, x, y + lineNumber * lineHeight);
  }
};

const slideMetric = (label, value) => ({
  label,
  value,
});

const getSlideMetrics = (data, section) => {
  const overview = data.overview || {};

  const engagement = data.engagement || {};

  const exits = data.exits || {};

  const journeys = data.journeys || {};

  const tools = data.tools || {};

  const search = data.search || {};

  const sets = {
    overview: [
      slideMetric("Visitors", num(overview.visitors?.current)),
      slideMetric("Sessions", num(overview.sessions?.current)),
      slideMetric("Page Views", num(overview.pageViews?.current)),
      slideMetric("Product Views", num(overview.productViews?.current)),
      slideMetric("Downloads", num(overview.downloads?.current)),
      slideMetric("Forms", num(overview.formSubmissions?.current)),
    ],

    pages: [
      slideMetric("Page Views", num(overview.pageViews?.current)),
      slideMetric("Visitors", num(overview.visitors?.current)),
      slideMetric("Pages / Session", Number(overview.pagesPerSession?.current || 0).toFixed(1)),
      slideMetric("Avg. Active", duration(overview.avgActiveSeconds?.current)),
    ],

    products: [
      slideMetric("Product Views", num(overview.productViews?.current)),
      slideMetric("Downloads", num(overview.downloads?.current)),
      slideMetric("Products", num(data.products?.length)),
      slideMetric("Forms", num(overview.formSubmissions?.current)),
    ],

    acquisition: [
      slideMetric("Visitors", num(overview.visitors?.current)),
      slideMetric("New Visitors", num(overview.newVisitors?.current)),
      slideMetric("Returning", num(overview.returningVisitors?.current)),
      slideMetric("Countries", num(data.acquisition?.countries?.length)),
    ],

    engagement: [
      slideMetric("Avg. Active", duration(overview.avgActiveSeconds?.current)),
      slideMetric("Pages / Session", Number(overview.pagesPerSession?.current || 0).toFixed(1)),
      slideMetric("Downloads", num(engagement.documentDownloads)),
      slideMetric("Forms", num(engagement.formSubmissions)),
      slideMetric("Email Clicks", num(engagement.emailClicks)),
      slideMetric("Phone Clicks", num(engagement.phoneClicks)),
    ],

    exits: [
      slideMetric("Eligible Exits", num(exits.eligible)),
      slideMetric("Tracked Actions", num(exits.tracked)),
      slideMetric("Contact Exits", num(exits.contact)),
      slideMetric("Passive Exits", num(exits.passive)),
      slideMetric("Avg. Pages", Number(exits.avgPages || 0).toFixed(1)),
      slideMetric("Avg. Active", duration(exits.avgActiveSeconds)),
    ],

    journeys: [
      slideMetric("Visitors", num(journeys.totalVisitors)),
      slideMetric("Returning", num(journeys.returningVisitors)),
      slideMetric("Return Rate", pct(journeys.returningRate)),
      slideMetric("Avg. Sessions", Number(journeys.avgSessions || 0).toFixed(1)),
      slideMetric("Avg. Pages", Number(journeys.avgPages || 0).toFixed(1)),
      slideMetric("Avg. Active", duration(journeys.avgActiveSeconds)),
    ],

    tools: [
      slideMetric("Tool Users", num(tools.users)),
      slideMetric("Lookup Starts", num(tools.starts)),
      slideMetric("Valid", num(tools.completions)),
      slideMetric("Completion", pct(tools.completionRate)),
      slideMetric("Invalid", num(tools.invalid)),
      slideMetric("Avg. Time", duration(tools.avgSeconds)),
    ],

    search: [
      slideMetric("Search Opens", num(search.opens)),
      slideMetric("Searches", num(search.searches)),
      slideMetric("Zero Results", num(search.zeroResults)),
      slideMetric("Result Clicks", num(search.resultClicks)),
      slideMetric("Search CTR", pct(search.ctr)),
      slideMetric("Pluma Messages", num(search.plumaMessages)),
    ],

    funnel: cleanRows(data.funnel, 6).map((row) => slideMetric(row.label, num(row.current))),
  };

  return sets[section] || [];
};

const drawKpiRow = (ctx, metrics) => {
  const left = 64;
  const top = 176;
  const gap = 18;
  const totalWidth = 1792;

  const width = (totalWidth - gap * (metrics.length - 1)) / Math.max(1, metrics.length);

  metrics.forEach((item, index) => {
    const x = left + index * (width + gap);

    ctx.fillStyle = "#f8fafc";

    drawRoundedRect(ctx, x, top, width, 120, 14);

    drawSlideText(ctx, item.label.toUpperCase(), x + 18, top + 17, {
      fontSize: 15,
      weight: 700,
      color: "#6b7280",
    });

    drawSlideText(ctx, item.value, x + 18, top + 49, {
      fontSize: 35,
      weight: 700,
      color: "#111827",
    });
  });
};

const drawBarPanel = (ctx, { title, rows, x, y, width, height, maxRows = 7 }) => {
  ctx.fillStyle = "#ffffff";

  drawRoundedRect(ctx, x, y, width, height, 16);

  ctx.strokeStyle = "#e5e7eb";

  ctx.lineWidth = 1;

  ctx.strokeRect(x, y, width, height);

  drawSlideText(ctx, title, x + 24, y + 22, {
    fontSize: 24,
    weight: 700,
    color: "#111827",
  });

  const items = cleanRows(rows, maxRows);

  const maxValue = Math.max(1, ...items.map((row) => Number(row.value ?? row.sessions ?? 0)));

  const rowHeight = (height - 82) / Math.max(1, maxRows);

  items.forEach((row, index) => {
    const rowY = y + 68 + rowHeight * index;

    const value = Number(row.value ?? row.sessions ?? 0);

    const label = clip(row.label || row.name || "Unknown", 29);

    drawSlideText(ctx, label, x + 24, rowY, {
      fontSize: 17,
      weight: 500,
      color: "#374151",
    });

    drawSlideText(ctx, num(value), x + width - 24, rowY, {
      fontSize: 17,
      weight: 700,
      color: "#111827",
      align: "right",
    });

    const barY = rowY + 27;

    const barWidth = width - 48;

    ctx.fillStyle = "#f1f5f9";

    drawRoundedRect(ctx, x + 24, barY, barWidth, 10, 5);

    ctx.fillStyle = index % 2 ? "#D94829" : "#85246D";

    drawRoundedRect(ctx, x + 24, barY, Math.max(5, (barWidth * value) / maxValue), 10, 5);
  });
};

const drawLinePanel = (ctx, rows, x, y, width, height) => {
  ctx.fillStyle = "#ffffff";

  drawRoundedRect(ctx, x, y, width, height, 16);

  ctx.strokeStyle = "#e5e7eb";

  ctx.strokeRect(x, y, width, height);

  drawSlideText(ctx, "Traffic Trend", x + 24, y + 22, {
    fontSize: 24,
    weight: 700,
    color: "#111827",
  });

  const values = cleanRows(rows, 90);

  if (!values.length) {
    return;
  }

  const chartX = x + 30;

  const chartY = y + 74;

  const chartW = width - 60;

  const chartH = height - 126;

  const maximum = Math.max(1, ...values.flatMap((row) => [Number(row.sessions || 0), Number(row.pageViews || 0)]));

  ctx.strokeStyle = "#e5e7eb";

  ctx.lineWidth = 2;

  ctx.beginPath();

  ctx.moveTo(chartX, chartY + chartH);

  ctx.lineTo(chartX + chartW, chartY + chartH);

  ctx.stroke();

  const drawSeries = (key, color) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 5;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    ctx.beginPath();

    values.forEach((row, index) => {
      const px = values.length === 1 ? chartX + chartW / 2 : chartX + (index / (values.length - 1)) * chartW;

      const py = chartY + chartH - (Number(row[key] || 0) / maximum) * chartH;

      if (!index) {
        ctx.moveTo(px, py);
      } else {
        ctx.lineTo(px, py);
      }
    });

    ctx.stroke();
  };

  drawSeries("sessions", "#D94829");

  drawSeries("pageViews", "#85246D");

  const first = values[0];

  const last = values[values.length - 1];

  drawSlideText(ctx, String(first.date || "").slice(5), chartX, chartY + chartH + 14, {
    fontSize: 15,
    color: "#6b7280",
  });

  drawSlideText(ctx, String(last.date || "").slice(5), chartX + chartW, chartY + chartH + 14, {
    fontSize: 15,
    color: "#6b7280",
    align: "right",
  });

  drawSlideText(ctx, "● Sessions", x + 24, y + height - 32, {
    fontSize: 15,
    weight: 600,
    color: "#D94829",
  });

  drawSlideText(ctx, "● Page Views", x + 135, y + height - 32, {
    fontSize: 15,
    weight: 600,
    color: "#85246D",
  });
};

const drawSimpleTable = (ctx, { title, headers, rows, x, y, width, height }) => {
  ctx.fillStyle = "#ffffff";

  drawRoundedRect(ctx, x, y, width, height, 16);

  ctx.strokeStyle = "#e5e7eb";

  ctx.strokeRect(x, y, width, height);

  drawSlideText(ctx, title, x + 24, y + 20, {
    fontSize: 24,
    weight: 700,
    color: "#111827",
  });

  const tableY = y + 65;

  const headerH = 42;

  ctx.fillStyle = "#f8fafc";

  ctx.fillRect(x + 18, tableY, width - 36, headerH);

  const colWidth = (width - 36) / headers.length;

  headers.forEach((header, index) => {
    drawSlideText(ctx, header, x + 24 + colWidth * index, tableY + 12, {
      fontSize: 14,
      weight: 700,
      color: "#6b7280",
    });
  });

  const displayRows = cleanRows(rows, 7);

  const rowH = (height - 125) / Math.max(1, displayRows.length);

  displayRows.forEach((row, rowIndex) => {
    const rowY = tableY + headerH + rowIndex * rowH;

    ctx.strokeStyle = "#e5e7eb";

    ctx.beginPath();

    ctx.moveTo(x + 18, rowY + rowH);

    ctx.lineTo(x + width - 18, rowY + rowH);

    ctx.stroke();

    row.forEach((cell, colIndex) => {
      ctx.font = `${colIndex === 0 ? 600 : 400} 15px Arial, Helvetica, sans-serif`;

      ctx.fillStyle = colIndex === 0 ? "#111827" : "#374151";

      ctx.textAlign = "left";

      wrapCanvasText(
        ctx,
        clip(cell, colIndex === 0 ? 34 : 22),
        x + 24 + colWidth * colIndex,
        rowY + 14,
        colWidth - 18,
        18,
        2
      );
    });
  });
};

const drawFunnelPanel = (ctx, rows, x, y, width, height) => {
  ctx.fillStyle = "#ffffff";

  drawRoundedRect(ctx, x, y, width, height, 16);

  ctx.strokeStyle = "#e5e7eb";

  ctx.strokeRect(x, y, width, height);

  drawSlideText(ctx, "Conversion Funnel", x + 24, y + 20, {
    fontSize: 24,
    weight: 700,
    color: "#111827",
  });

  const values = cleanRows(rows, 6);

  const available = height - 100;

  const stageH = available / Math.max(1, values.length);

  values.forEach((row, index) => {
    const ratio = Math.max(0.5, 1 - index * 0.1);

    const stageW = (width - 80) * ratio;

    const stageX = x + width / 2 - stageW / 2;

    const stageY = y + 70 + index * stageH;

    ctx.fillStyle = index % 2 ? "#D94829" : "#85246D";

    drawRoundedRect(ctx, stageX, stageY, stageW, Math.max(42, stageH - 10), 8);

    drawSlideText(ctx, row.label, stageX + 16, stageY + 10, {
      fontSize: 17,
      weight: 700,
      color: "#ffffff",
    });

    drawSlideText(ctx, num(row.current), stageX + stageW - 16, stageY + 10, {
      fontSize: 19,
      weight: 700,
      color: "#ffffff",
      align: "right",
    });
  });
};

const drawSlideHeader = async (ctx, data, title) => {
  ctx.fillStyle = "#ffffff";

  ctx.fillRect(0, 0, SLIDE_WIDTH, SLIDE_HEIGHT);

  ctx.fillStyle = "#D94829";

  ctx.fillRect(0, 0, SLIDE_WIDTH, 10);

  let logoImage = null;

  try {
    const logoData = await loadLogo(data.brand?.logoUrl);

    if (logoData) {
      logoImage = await dataUrlToImage(logoData);
    }
  } catch (error) {
    console.warn("Slide logo could not be loaded:", error);
  }

  if (logoImage) {
    ctx.drawImage(logoImage, 64, 34, 210, 60);
  } else {
    drawSlideText(ctx, BRAND.name, 64, 44, {
      fontSize: 30,
      weight: 700,
      color: "#111827",
    });
  }

  drawSlideText(ctx, title, 320, 42, {
    fontSize: 34,
    weight: 700,
    color: "#111827",
  });

  drawSlideText(ctx, reportRange(data), 1850, 48, {
    fontSize: 18,
    weight: 600,
    color: "#6b7280",
    align: "right",
  });

  ctx.strokeStyle = "#e5e7eb";

  ctx.lineWidth = 2;

  ctx.beginPath();

  ctx.moveTo(64, 122);

  ctx.lineTo(1856, 122);

  ctx.stroke();
};

const drawSlideFooter = (ctx, data) => {
  ctx.strokeStyle = "#e5e7eb";

  ctx.beginPath();

  ctx.moveTo(64, 1024);

  ctx.lineTo(1856, 1024);

  ctx.stroke();

  drawSlideText(ctx, "Go!Foton Website Analytics · Internal", 64, 1040, {
    fontSize: 15,
    color: "#6b7280",
  });

  drawSlideText(ctx, `Generated ${generatedDate(data.generatedAt)}`, 1856, 1040, {
    fontSize: 15,
    color: "#6b7280",
    align: "right",
  });
};

export async function exportAnalyticsSlide({ data, sectionKey = "overview" }) {
  if (!data) {
    throw new Error("Analytics report data is not available yet.");
  }

  const title = SLIDE_TITLES[sectionKey];

  if (!title) {
    throw new Error("This analytics section does not have a slide image layout.");
  }

  const canvas = document.createElement("canvas");

  canvas.width = SLIDE_WIDTH;

  canvas.height = SLIDE_HEIGHT;

  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("The browser could not create the slide image.");
  }

  await drawSlideHeader(ctx, data, title);

  drawKpiRow(ctx, getSlideMetrics(data, sectionKey));

  const contentY = 326;
  const contentH = 660;

  if (sectionKey === "overview") {
    drawLinePanel(ctx, data.trends?.traffic || [], 64, contentY, 1120, contentH);

    drawBarPanel(ctx, {
      title: "Top Traffic Sources",

      rows: data.acquisition?.sources || [],

      x: 1210,
      y: contentY,
      width: 646,
      height: 310,
      maxRows: 5,
    });

    drawBarPanel(ctx, {
      title: "Top Products",

      rows: (data.products || []).map((row) => ({
        label: row.name,
        value: row.views,
      })),

      x: 1210,
      y: contentY + 335,
      width: 646,
      height: 325,
      maxRows: 5,
    });
  } else if (sectionKey === "pages") {
    drawSimpleTable(ctx, {
      title: "Page Performance",

      headers: ["Page", "Visitors", "Views", "Active", "Scroll"],

      rows: cleanRows(data.pages, 7).map((row) => [
        row.page,
        num(row.uniqueVisitors),
        num(row.views),
        duration(row.avgActiveSeconds),
        pct(row.avgScroll, 0),
      ]),

      x: 64,
      y: contentY,
      width: 1180,
      height: contentH,
    });

    drawBarPanel(ctx, {
      title: "Top Pages by Views",

      rows: (data.pages || []).map((row) => ({
        label: row.page,
        value: row.views,
      })),

      x: 1270,
      y: contentY,
      width: 586,
      height: contentH,
      maxRows: 8,
    });
  } else if (sectionKey === "products") {
    drawSimpleTable(ctx, {
      title: "Product Performance",

      headers: ["Product", "Family", "Visitors", "Views", "Downloads"],

      rows: cleanRows(data.products, 7).map((row) => [
        row.name,
        row.family || "—",
        num(row.visitors),
        num(row.views),
        num(row.downloads),
      ]),

      x: 64,
      y: contentY,
      width: 1180,
      height: contentH,
    });

    drawBarPanel(ctx, {
      title: "Top Products by Views",

      rows: (data.products || []).map((row) => ({
        label: row.name,
        value: row.views,
      })),

      x: 1270,
      y: contentY,
      width: 586,
      height: contentH,
      maxRows: 8,
    });
  } else if (sectionKey === "acquisition") {
    drawBarPanel(ctx, {
      title: "Top Acquisition Sources",
      rows: data.acquisition?.sources || [],
      x: 64,
      y: contentY,
      width: 570,
      height: contentH,
      maxRows: 8,
    });

    drawBarPanel(ctx, {
      title: "Top Countries",
      rows: data.acquisition?.countries || [],
      x: 660,
      y: contentY,
      width: 570,
      height: contentH,
      maxRows: 8,
    });

    drawBarPanel(ctx, {
      title: "Top Landing Pages",
      rows: (data.acquisition?.landingPages || []).map((row) => ({
        label: row.page,
        value: row.sessions,
      })),
      x: 1256,
      y: contentY,
      width: 600,
      height: contentH,
      maxRows: 8,
    });
  } else if (sectionKey === "engagement") {
    drawBarPanel(ctx, {
      title: "Engagement by Weekday",
      rows: data.engagement?.weekday || [],
      x: 64,
      y: contentY,
      width: 560,
      height: contentH,
      maxRows: 7,
    });

    drawFunnelPanel(ctx, data.funnel || [], 650, contentY, 560, contentH);

    drawBarPanel(ctx, {
      title: "Top Downloads",
      rows: data.engagement?.topDownloads || [],
      x: 1236,
      y: contentY,
      width: 620,
      height: contentH,
      maxRows: 7,
    });
  } else if (sectionKey === "funnel") {
    drawFunnelPanel(ctx, data.funnel || [], 250, contentY, 1420, contentH);
  } else if (sectionKey === "journeys") {
    drawBarPanel(ctx, {
      title: "Journey Classifications",
      rows: data.journeys?.categories || [],
      x: 64,
      y: contentY,
      width: 650,
      height: contentH,
      maxRows: 7,
    });

    drawSimpleTable(ctx, {
      title: "Longest Active Journeys",

      headers: ["Visitor", "Type", "Sessions", "Pages", "Active"],

      rows: cleanRows(data.journeys?.longest, 3).map((row) => [
        row.visitor,
        row.category,
        num(row.sessions),
        num(row.pages),
        duration(row.activeSeconds),
      ]),

      x: 740,
      y: contentY,
      width: 1116,
      height: contentH,
    });
  } else if (sectionKey === "tools") {
    drawLinePanel(
      ctx,
      (data.tools?.trend || []).map((row) => ({
        date: row.date,
        sessions: row.started,
        pageViews: row.completed,
      })),
      64,
      contentY,
      1080,
      contentH
    );

    drawBarPanel(ctx, {
      title: "Most Configured Products",
      rows: data.tools?.topProducts || [],
      x: 1170,
      y: contentY,
      width: 686,
      height: 310,
      maxRows: 6,
    });

    drawBarPanel(ctx, {
      title: "Most Completed Part Numbers",
      rows: data.tools?.topParts || [],
      x: 1170,
      y: contentY + 335,
      width: 686,
      height: 325,
      maxRows: 6,
    });
  } else if (sectionKey === "search") {
    drawBarPanel(ctx, {
      title: "Top Search Terms",
      rows: data.search?.topTerms || [],
      x: 64,
      y: contentY,
      width: 860,
      height: contentH,
      maxRows: 8,
    });

    drawBarPanel(ctx, {
      title: "Most-Clicked Results",
      rows: data.search?.topResults || [],
      x: 950,
      y: contentY,
      width: 906,
      height: contentH,
      maxRows: 8,
    });
  } else if (sectionKey === "exits") {
    drawBarPanel(ctx, {
      title: "Why Sessions Ended",
      rows: data.exits?.reasons || [],
      x: 64,
      y: contentY,
      width: 870,
      height: contentH,
      maxRows: 8,
    });

    drawBarPanel(ctx, {
      title: "Top Exit Pages",
      rows: data.exits?.topPages || [],
      x: 960,
      y: contentY,
      width: 896,
      height: contentH,
      maxRows: 8,
    });
  }

  drawSlideFooter(ctx, data);

  const blob = await canvasToBlob(canvas, "image/jpeg", 0.94);

  downloadBlob(blob, `gofoton-${safeName(sectionKey)}-${safeName(data.range?.label || "analytics")}.jpg`);
}
